import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
//#region types
import { WintSuperkollAPIModelTimeReporting as ApiModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { TimeReportingProjectFormTypes } from "models/formTypes";
import { TableRowCommonKeys, TableRowProperties } from "models/propTypes/simpleTableProps";
import { HourEnums } from "models/enums";
import { Amount } from "models/utilTypes/currencyType";
import { Hours } from "models/utilTypes/hourType";
import { RouteConstants } from "utils/constants";
//#endregion
//#region components
import { TableAsync, useFilterContext, useSearchContext } from "components/common";
import ProjectStatusTags from "./projectStatusTag";
import { useTimeReportingProjectService } from "serviceHooks";
//#endregion
import { DateHelper, UrlHelper } from "utils/helpers";

const TimeReportingProjectList: React.FC = () => {
  const timeReportingProjectService = useTimeReportingProjectService();
  const location = useLocation();
  const navigate = useNavigate();
  const { filter } = useFilterContext<TimeReportingProjectFormTypes.Filter>();
  const searchContext = useSearchContext();

  const filterFunc = React.useCallback(
    (pagination: WintUtilsWebApi.Filter) => {
      return timeReportingProjectService.getTimeReportingProjectsReport(pagination, {
        ...(filter ?? {}),
        projectName: searchContext?.searchText,
        includeDeletedProjects: filter?.includeDeletedProjects as boolean,
        includeInactiveProjects: filter?.includeInactiveProjects as boolean,
      });
    },
    [filter, searchContext?.searchText]
  );

  const onRowClick = (row: ApiModel.TimeReportingProjectReport) => {
    const periodQuery = DateHelper.splitDateToYearMonthByCharacter(
      filter?.period ?? DateHelper.getFirstDayOfPreviousMonth(new Date())
    );
    navigate(
      UrlHelper.formatPath(`/Projects/${row.id}?${RouteConstants.TimeReportingQueryParams.Period}=${periodQuery}`),
      {
        state: { background: location, preserveSearch: true },
      }
    );
  };

  const tableProperties: Partial<
    Record<keyof TableRowCommonKeys | keyof ApiModel.TimeReportingProjectReport, TableRowProperties>
  > = {
    name: {
      description: TranslationKeys.TimeReportingProject.Name,
      helpText: TranslationKeys.TimeReportingProject.HelperTextName,
    },
    managerName: {
      description: TranslationKeys.TimeReportingProject.ManagerName,
      helpText: TranslationKeys.TimeReportingProject.HelperTextManagerName,
    },
    numberOfMembersInProject: {
      description: TranslationKeys.TimeReportingProject.NumberOfMembers,
      helpText: TranslationKeys.TimeReportingProject.HelperTextNumberOfMembers,
      alignment: "right",
      classification: "count",
    },
    numberOfHoursWorked: {
      description: TranslationKeys.TimeReportingProject.NumberOfHoursWorked,
      helpText: TranslationKeys.TimeReportingProject.HelperTextNumberOfHoursWorked,
      formatter: (r: ApiModel.TimeReportingProjectReport) => new Hours(r.numberOfHoursWorked, HourEnums.HourCode.SHORT),
      alignment: "right",
      classification: "time",
    },
    leftToInvoice: {
      description: TranslationKeys.TimeReportingProject.LeftToInvoice,
      helpText: TranslationKeys.TimeReportingProject.HelperTextLeftToInvoice,
      formatter: (r: ApiModel.TimeReportingProjectReport) => new Amount(r.currencyCode, r.leftToInvoice),
      classification: "amount",
    },
    invoiced: {
      description: TranslationKeys.TimeReportingProject.Invoiced,
      helpText: TranslationKeys.TimeReportingProject.HelperTextInvoiced,
      formatter: (r: ApiModel.TimeReportingProjectReport) => new Amount(r.currencyCode, r.invoiced),
      classification: "amount",
    },
    projectActiveState: {
      description: TranslationKeys.Common.Status,
      helpText: TranslationKeys.TimeReportingProject.ProjectActiveStateHelperText,
      formatter: (r: ApiModel.TimeReportingProjectReport) => (
        <ProjectStatusTags projectActiveState={r.projectActiveState}></ProjectStatusTags>
      ),
    },
  };

  return (
    <TableAsync<ApiModel.TimeReportingProjectReport>
      properties={tableProperties}
      filter={filterFunc}
      rowClickHandler={onRowClick}
      pagination={"advanced"}
      maxRowCount={10}
      orderByProperty="name"
      orderByDescending={false}
    />
  );
};
export default TimeReportingProjectList;
